/**
 * i18n Configuration
 *
 * Internationalization setup using i18next + react-i18next.
 * All user-facing text should use translation keys.
 *
 * Usage in components:
 *   import { useTranslation } from 'react-i18next';
 *
 *   const { t } = useTranslation('dashboard');
 *   <Typography>{t('title')}</Typography>
 *   <Typography>{t('kpis.revenue.change', { percent: 12 })}</Typography>
 *
 * Cross-namespace:
 *   <Button>{t('common:actions.save')}</Button>
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// v2.55 - Added auth namespace for Clerk authentication - 2026-02-04
// Import English locale files
import enCommon from './locales/en/common.json';
import enNavigation from './locales/en/navigation.json';
import enLanding from './locales/en/landing.json';
import enDashboard from './locales/en/dashboard.json';
import enDataSources from './locales/en/dataSources.json';
import enChat from './locales/en/chat.json';
import enOnboarding from './locales/en/onboarding.json';
import enAuth from './locales/en/auth.json';
import enCleanup from './locales/en/cleanup.json'; // v4.36 - EXPO-QUALIFIER-01 - 2026-10-29

// Initialize i18next
i18n
  .use(LanguageDetector) // Detect user language from browser
  .use(initReactI18next) // Pass to react-i18next
  .init({
    resources: {
      en: {
        common: enCommon,
        navigation: enNavigation,
        landing: enLanding,
        dashboard: enDashboard,
        dataSources: enDataSources,
        chat: enChat,
        onboarding: enOnboarding,
        auth: enAuth,
        cleanup: enCleanup, // v4.36 - EXPO-QUALIFIER-01
      },
      // Future languages can be added here:
      // es: { common: esCommon, ... },
      // fr: { common: frCommon, ... },
    },

    // Default language
    fallbackLng: 'en',

    // Default namespace (used when no namespace specified)
    defaultNS: 'common',

    // Namespaces to load initially
    ns: ['common', 'navigation', 'landing', 'dashboard', 'dataSources', 'chat', 'onboarding', 'auth', 'cleanup'], // v4.36 - EXPO-QUALIFIER-01: cleanup added

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already handles XSS
    },

    // Detection settings
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    // React settings
    react: {
      useSuspense: true,
    },
  });

export default i18n;

// Helper to change language programmatically
export const changeLanguage = (lng: string) => {
  return i18n.changeLanguage(lng);
};

// Helper to get current language
export const getCurrentLanguage = () => {
  return i18n.language;
};

// Available languages
export const availableLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  // { code: 'es', name: 'Spanish', nativeName: 'Español' },
  // { code: 'fr', name: 'French', nativeName: 'Français' },
];
